import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore, useBrandStore } from "@/store";
import { settingsAPI } from "@/services/api";

// Pages
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Tracks from "@/pages/Tracks";
import Reports from "@/pages/Reports";
import Video from "@/pages/Video";
import Alerts from "@/pages/Alerts";
import Settings from "@/pages/Settings";
import BridgeSettings from "@/pages/BridgeSettings";
import UsersManagement from "@/pages/Users";
import Classic from "@/pages/Classic";
import LiveVideoPage from "@/pages/LiveVideoPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  const token = localStorage.getItem('4sat_token');
  
  if (!isAuthenticated && !token) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  const token = localStorage.getItem('4sat_token');
  const versionMode = localStorage.getItem('4sat_version_mode');
  
  if (isAuthenticated || token) {
    // Redirect based on version preference
    if (versionMode === 'classic') {
      return <Navigate to="/classic" replace />;
    }
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  const { login, isAuthenticated } = useAuthStore();
  const { setBrandSettings } = useBrandStore();
  
  // Check for existing session on mount
  useEffect(() => {
    const token = localStorage.getItem('4sat_token');
    const user = localStorage.getItem('4sat_user');
    
    if (token && user) {
      try {
        login(token, JSON.parse(user));
      } catch (e) {
        localStorage.removeItem('4sat_token');
        localStorage.removeItem('4sat_user');
      }
    }
  }, [login]);

  // Load brand settings from database when authenticated
  useEffect(() => {
    const loadBrandSettings = async () => {
      try {
        const token = localStorage.getItem('4sat_token');
        if (!token) return;
        
        const response = await settingsAPI.getBrand();
        if (response.data) {
          const data = response.data;
          setBrandSettings({
            logoUrl: data.logo_url || null,
            companyName: data.company_name || '4Sat',
            primaryColor: data.primary_color || '#3b82f6',
            secondaryColor: data.secondary_color || '#10b981',
            loginLogoSize: data.login_logo_size || 'medium',
          });
          console.log('Brand settings loaded from database');
        }
      } catch (err) {
        console.log('Could not load brand settings from database:', err.message);
      }
    };

    // Load on mount if authenticated
    const token = localStorage.getItem('4sat_token');
    if (token || isAuthenticated) {
      loadBrandSettings();
    }
  }, [isAuthenticated, setBrandSettings]);

  return (
    <div className="App dark">
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />
          
          {/* Public Live Video Page - For 4sat.cl direct access */}
          <Route 
            path="/live/:imei" 
            element={<LiveVideoPage />} 
          />
          
          {/* Protected Routes */}
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/tracks/:imei" 
            element={
              <ProtectedRoute>
                <Tracks />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/tracks" 
            element={
              <ProtectedRoute>
                <Tracks />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/reports" 
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/video/:imei" 
            element={
              <ProtectedRoute>
                <Video />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/video" 
            element={
              <ProtectedRoute>
                <Video />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/alerts" 
            element={
              <ProtectedRoute>
                <Alerts />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/bridge" 
            element={
              <ProtectedRoute>
                <BridgeSettings />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/users" 
            element={
              <ProtectedRoute>
                <UsersManagement />
              </ProtectedRoute>
            } 
          />
          
          {/* Classic Mode - Tracksolid iframe */}
          <Route 
            path="/classic" 
            element={
              <ProtectedRoute>
                <Classic />
              </ProtectedRoute>
            } 
          />
          
          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
